/**
 * My Shopstop V619 — Kategorien für dich carousel.
 * Native horizontal scrolling with desktop controls, touch scrolling and
 * damped mouse dragging with momentum on desktop, owned by the component.
 */
(() => {
  if (window.customElements.get('ss-category-orbit-carousel')) return;

  const desktopQuery = window.matchMedia('(min-width: 761px)');
  const pointerDragQuery = window.matchMedia('(min-width: 1101px)');
  const compactMobileQuery = window.matchMedia('(max-width: 760px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const edgeTolerance = 2;
  const dragThreshold = 5;
  const dragFactor = 0.82;

  class SSCategoryOrbitCarousel extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-ss-category-orbit-rail]');
      this.previousButton = this.querySelector('[data-ss-category-orbit-previous]');
      this.nextButton = this.querySelector('[data-ss-category-orbit-next]');

      if (!this.rail || !this.previousButton || !this.nextButton) return;

      this.dragState = null;
      this.suppressClick = false;
      this.motionFrame = null;

      this.onPreviousClick = () => this.move(-1);
      this.onNextClick = () => this.move(1);
      this.onLayoutChange = () => this.requestUpdate();
      this.onNativeDragStart = (event) => {
        if (event.target.closest('a, img, button')) event.preventDefault();
      };
      this.onPointerDown = (event) => this.startPointerDrag(event);
      this.onPointerMove = (event) => this.updatePointerDrag(event);
      this.onPointerUp = (event) => this.finishPointerDrag(event);
      this.onClickCapture = (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      };

      this.previousButton.addEventListener('click', this.onPreviousClick);
      this.nextButton.addEventListener('click', this.onNextClick);
      this.rail.addEventListener('scroll', this.onLayoutChange, { passive: true });
      this.rail.addEventListener('dragstart', this.onNativeDragStart);
      this.rail.addEventListener('pointerdown', this.onPointerDown);
      this.rail.addEventListener('pointermove', this.onPointerMove);
      this.rail.addEventListener('pointerup', this.onPointerUp);
      this.rail.addEventListener('pointercancel', this.onPointerUp);
      this.rail.addEventListener('lostpointercapture', this.onPointerUp);
      this.rail.addEventListener('click', this.onClickCapture, true);

      if (desktopQuery.addEventListener) {
        desktopQuery.addEventListener('change', this.onLayoutChange);
        compactMobileQuery.addEventListener('change', this.onLayoutChange);
      } else {
        desktopQuery.addListener(this.onLayoutChange);
        compactMobileQuery.addListener(this.onLayoutChange);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onLayoutChange);
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.rail);
      } else {
        window.addEventListener('resize', this.onLayoutChange, { passive: true });
      }

      this.requestUpdate();
    }

    disconnectedCallback() {
      if (!this.rail) return;

      this.previousButton.removeEventListener('click', this.onPreviousClick);
      this.nextButton.removeEventListener('click', this.onNextClick);
      this.rail.removeEventListener('scroll', this.onLayoutChange);
      this.rail.removeEventListener('dragstart', this.onNativeDragStart);
      this.rail.removeEventListener('pointerdown', this.onPointerDown);
      this.rail.removeEventListener('pointermove', this.onPointerMove);
      this.rail.removeEventListener('pointerup', this.onPointerUp);
      this.rail.removeEventListener('pointercancel', this.onPointerUp);
      this.rail.removeEventListener('lostpointercapture', this.onPointerUp);
      this.rail.removeEventListener('click', this.onClickCapture, true);

      if (desktopQuery.removeEventListener) {
        desktopQuery.removeEventListener('change', this.onLayoutChange);
        compactMobileQuery.removeEventListener('change', this.onLayoutChange);
      } else {
        desktopQuery.removeListener(this.onLayoutChange);
        compactMobileQuery.removeListener(this.onLayoutChange);
      }

      if (this.resizeObserver) this.resizeObserver.disconnect();
      else window.removeEventListener('resize', this.onLayoutChange);

      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
      this.updateFrame = null;
      this.stopMotion();
      this.dragState = null;
      this.suppressClick = false;
      this.rail.classList.remove('is-dragging');
    }

    requestUpdate() {
      if (this.updateFrame) return;
      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.updateState();
      });
    }

    updateState() {
      const railStyles = window.getComputedStyle(this.rail);

      if (compactMobileQuery.matches) {
        this.rail.style.removeProperty('--ss-category-item-width');
        this.style.removeProperty('--ss-category-media-size');
      } else {
        const columns = Math.max(1, Number.parseInt(railStyles.getPropertyValue('--ss-category-columns'), 10) || 7);
        const gap = Number.parseFloat(railStyles.columnGap || railStyles.gap) || 0;
        const itemWidth = Math.max(1, (this.rail.clientWidth - gap * (columns - 1)) / columns);
        const mediaMax = Number.parseFloat(window.getComputedStyle(this).getPropertyValue('--ss-category-media-max')) || 194;
        const mediaSize = Math.min(itemWidth, mediaMax);
        this.rail.style.setProperty('--ss-category-item-width', `${itemWidth}px`);
        this.style.setProperty('--ss-category-media-size', `${mediaSize}px`);
      }

      const maximum = this.getMaximumScroll();
      const hasOverflow = maximum > edgeTolerance;
      const canScrollBack = hasOverflow && this.rail.scrollLeft > edgeTolerance;
      const canScrollForward = hasOverflow && this.rail.scrollLeft < maximum - edgeTolerance;
      const showDesktopControls = desktopQuery.matches && hasOverflow;

      this.previousButton.hidden = !showDesktopControls;
      this.nextButton.hidden = !showDesktopControls;
      this.previousButton.disabled = !canScrollBack;
      this.nextButton.disabled = !canScrollForward;
      this.previousButton.setAttribute('aria-hidden', String(!showDesktopControls));
      this.nextButton.setAttribute('aria-hidden', String(!showDesktopControls));
    }

    getMaximumScroll() {
      return Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
    }

    clamp(value, minimum, maximum) {
      return Math.min(Math.max(value, minimum), maximum);
    }

    stopMotion() {
      if (this.motionFrame !== null) {
        cancelAnimationFrame(this.motionFrame);
        this.motionFrame = null;
      }
    }

    startMomentum(initialVelocity) {
      this.stopMotion();
      if (reducedMotionQuery.matches) return;

      let speed = this.clamp(initialVelocity * 0.78, -1.1, 1.1);
      if (Math.abs(speed) < 0.02) return;

      let previousTime = performance.now();

      const frame = (now) => {
        const elapsed = Math.min(now - previousTime, 32);
        previousTime = now;

        const previousLeft = this.rail.scrollLeft;
        this.rail.scrollLeft += speed * elapsed;
        const maximum = this.getMaximumScroll();

        if (
          this.rail.scrollLeft <= 0 ||
          this.rail.scrollLeft >= maximum ||
          this.rail.scrollLeft === previousLeft
        ) {
          speed = 0;
        } else {
          speed *= Math.pow(0.945, elapsed / 16.67);
        }

        if (Math.abs(speed) > 0.012) {
          this.motionFrame = requestAnimationFrame(frame);
        } else {
          this.motionFrame = null;
          this.requestUpdate();
        }
      };

      this.motionFrame = requestAnimationFrame(frame);
    }

    startPointerDrag(event) {
      if (!pointerDragQuery.matches || event.pointerType !== 'mouse' || event.button !== 0) return;
      if (this.getMaximumScroll() <= edgeTolerance) return;

      this.stopMotion();
      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: this.rail.scrollLeft,
        lastX: event.clientX,
        lastTime: performance.now(),
        velocity: 0,
        moved: false
      };
    }

    updatePointerDrag(event) {
      const state = this.dragState;
      if (!state || state.pointerId !== event.pointerId) return;

      const rawDistance = event.clientX - state.startX;
      if (!state.moved && Math.abs(rawDistance) < dragThreshold) return;

      if (!state.moved) {
        state.moved = true;
        this.rail.classList.add('is-dragging');

        if (this.rail.setPointerCapture && !this.rail.hasPointerCapture(event.pointerId)) {
          this.rail.setPointerCapture(event.pointerId);
        }
      }

      const distance = rawDistance * dragFactor;
      const now = performance.now();
      const elapsed = Math.max(now - state.lastTime, 1);
      const scrollDelta = (state.lastX - event.clientX) * dragFactor;
      const instantVelocity = scrollDelta / elapsed;

      this.rail.scrollLeft = state.startScrollLeft - distance;
      state.velocity = (state.velocity * 0.72) + (instantVelocity * 0.28);
      state.lastX = event.clientX;
      state.lastTime = now;
      event.preventDefault();
    }

    finishPointerDrag(event) {
      const state = this.dragState;
      if (!state || (event && state.pointerId !== event.pointerId)) return;

      this.suppressClick = state.moved;
      this.dragState = null;
      this.rail.classList.remove('is-dragging');

      if (
        this.rail.releasePointerCapture &&
        this.rail.hasPointerCapture(state.pointerId)
      ) {
        this.rail.releasePointerCapture(state.pointerId);
      }

      if (performance.now() - state.lastTime > 80) state.velocity = 0;
      if (state.moved) this.startMomentum(state.velocity);

      window.setTimeout(() => {
        this.suppressClick = false;
      }, 0);

      this.requestUpdate();
    }

    getScrollStep() {
      const firstItem = this.rail.querySelector('.ss-category-orbit__item');
      if (!firstItem) return this.rail.clientWidth;

      const railStyles = window.getComputedStyle(this.rail);
      const gap = Number.parseFloat(railStyles.columnGap || railStyles.gap) || 0;
      const itemWidth = firstItem.getBoundingClientRect().width;
      const visibleItems = Math.max(1, Math.floor((this.rail.clientWidth + gap) / (itemWidth + gap)));

      return (itemWidth + gap) * visibleItems;
    }

    move(direction) {
      this.stopMotion();
      this.rail.scrollBy({
        left: direction * this.getScrollStep(),
        behavior: reducedMotionQuery.matches ? 'auto' : 'smooth'
      });
    }
  }

  window.customElements.define('ss-category-orbit-carousel', SSCategoryOrbitCarousel);
})();
